import invariant from 'invariant';
import get from 'lodash/get';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { ApiAddressWithFormData } from '#/api/types/ApiAddress';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { Nullable } from '#/types/Nullable';
import { lookupAddressWithState } from '#/utils/address/lookupAddressWithState';
import { filterObjectByKey } from '#/utils/functional/object';
import { isEmpty } from '#/utils/functional/poly';
import {
  setGMapsError,
  useGMapsErrored,
} from '#/zustand-stores/gMapsErrorStore';
import { showModal } from '#/zustand-stores/modalStore';

import { AddressFields } from './AddressFields';

type Props = {
  showDescription?: boolean;
  canChangeLocationHidden?: boolean;
  address: ApiAddressWithFormData | null | undefined;
  updateAddress: (update: Nullable<Partial<ApiAddressWithFormData>>) => void;
  fetchingAddress: boolean;
  setFetchingAddress: (arg0: boolean) => void;
  clearErrors: () => void;
  error: string | null | undefined;
  disabled?: boolean;
  orgAddress?: ApiAddressWithFormData;
  showLocationButtonText?: string;
  hideLocationButtonText?: string;
  index?: number;
  toggleSeparateFields: () => void;
  errors: Record<string, unknown>;
  disableOrgAddressButton?: boolean;
  label?: ReactNode;
};

const makeCityOnly = ({
  city,
  stateCode,
  country,
}: Readonly<{
  city?: string | null | undefined;
  stateCode?: string | null | undefined;
  country?: string | null | undefined;
}>) =>
  city && stateCode && country ? `${city}, ${stateCode}, ${country}` : '';

export function AddressFormContainerInternal({
  showDescription,
  canChangeLocationHidden,
  address,
  updateAddress,
  fetchingAddress,
  setFetchingAddress,
  clearErrors,
  error,
  disabled = false,
  orgAddress,
  showLocationButtonText,
  hideLocationButtonText,
  index,
  toggleSeparateFields,
  errors,
  disableOrgAddressButton = false,
  label,
}: Props) {
  const { i18n } = useI18n();

  const { gMapsErrored } = useGMapsErrored();

  const [gMapsErrorModalShown, setGMapsErrorModalShown] = useState(false);
  const namePrefix = index !== undefined ? `addresses.${index}` : 'address';
  useEffect(() => {
    const keys = [
      'line1',
      'line2',
      'city',
      'state',
      'stateCode',
      'zipCode',
      'full',
    ];
    const currentErrors = keys.filter((key) =>
      Object.keys(errors).includes(`${namePrefix}.${key}`),
    );

    if (currentErrors.length > 0 && !currentErrors.includes('full')) {
      toggleSeparateFields();
    }
  }, [errors, namePrefix, toggleSeparateFields]);
  // Has the user selected a suggestion and not manually edited it afterwards?
  const cityOnly = get(address, 'locationHidden');
  const fullAddress = get(address, ['full']);
  const [suggestionSelected, setSuggestionSelected] = useState(fullAddress);
  const [editedValue, setEditedValue] = useState(fullAddress);

  const applyUpdate = useCallback(
    (addressUpdate: ApiAddressWithFormData) => {
      const addressUpdateIsCity = isEmpty(addressUpdate.line1);
      let update = addressUpdate;

      if (cityOnly) {
        if (addressUpdateIsCity) {
          update = filterObjectByKey<ApiAddressWithFormData>(
            addressUpdate,
            (k) =>
              [
                'city',
                'state',
                'stateCode',
                'latitude',
                'longitude',
                'latLongAddress',
                'country',
              ].includes(k),
          );
        } else {
          update = filterObjectByKey(addressUpdate, (k) =>
            ['city', 'state', 'stateCode', 'country'].includes(k),
          );
        }
      }

      const full =
        cityOnly && !addressUpdateIsCity
          ? makeCityOnly(addressUpdate)
          : (addressUpdate.latLongAddress ?? addressUpdate.full);
      setEditedValue(full);
      setSuggestionSelected(full);
      updateAddress({ ...update, full });

      if (cityOnly && !addressUpdateIsCity) {
        setFetchingAddress(true);
        const updatedAddress = { ...address, ...update };

        lookupAddressWithState({
          query: full || '',
          state: updatedAddress.state,
          country: updatedAddress.country,
        })
          .then((addressResults) => {
            updateAddress(addressResults);
          })
          .catch((errorMessage) => setGMapsError(errorMessage))
          .finally(() => setFetchingAddress(false));
      }
    },
    [address, cityOnly, setFetchingAddress, updateAddress],
  );

  useEffect(() => {
    if (
      address?.full &&
      !address.latitude &&
      !address.longitude &&
      !fetchingAddress
    ) {
      setFetchingAddress(true);
      lookupAddressWithState({
        query: address.full,
        state: address.state,
        country: address.country,
      })
        .then((addressResults) => {
          applyUpdate(addressResults);
        })
        .catch((errorMessage) => setGMapsError(errorMessage))
        .finally(() => setFetchingAddress(false));
    }
  }, [address, applyUpdate, fetchingAddress, setFetchingAddress]);

  const selectAutoCompleteSuggestion = (
    addressUpdate: Record<string, unknown>,
  ) => {
    applyUpdate(addressUpdate);
  };

  const useOrgAddress = () => {
    invariant(orgAddress, 'Missing orgAddress');
    applyUpdate(orgAddress);
  };

  const overrideLatLong = ({
    latitude,
    longitude,
  }: {
    latitude?: number;
    longitude?: number;
  }) => {
    updateAddress({
      latitude,
      longitude,
      latLongAddress: null,
    });
  };

  useEffect(() => {
    if (gMapsErrored && !gMapsErrorModalShown) {
      showModal('GENERIC_UNCLOSEABLE', {
        text: i18n(
          'Our geolocation provider (Google Maps) did not load correctly. Please reload the page.',
        ),
      });
      setGMapsErrorModalShown(true);
    }
  }, [i18n, gMapsErrored, gMapsErrorModalShown]);

  const onManuallyModifiedValue = (
    serializedAddressComponent: SearchLocation | undefined,
  ) => {
    if (suggestionSelected) {
      updateAddress({
        line1: null,
        line2: null,
        city: null,
        state: null,
        stateCode: null,
        zipcode: null,
        latLongAddress: '',
        latitude: null,
        longitude: null,
        full: null,
      });
      setSuggestionSelected(undefined);
    }

    setEditedValue(serializedAddressComponent?.full);
  };

  const onLocationHiddenChange = () => {
    updateAddress({
      line1: null,
      line2: null,
      city: null,
      state: null,
      stateCode: null,
      zipcode: null,
      latLongAddress: '',
      latitude: null,
      longitude: null,
      full: null,
    });
    setEditedValue('');
  };

  return (
    <AddressFields
      onLocationHiddenChange={onLocationHiddenChange}
      editedValue={editedValue}
      clearErrors={clearErrors}
      cityOnly={Boolean(address?.locationHidden)}
      onManualModifiedValue={onManuallyModifiedValue}
      address={address}
      namePrefix={namePrefix}
      showDescription={showDescription}
      canChangeLocationHidden={canChangeLocationHidden}
      selectAutoCompleteSuggestion={selectAutoCompleteSuggestion}
      overrideLatLong={overrideLatLong}
      error={error}
      disabled={disabled}
      useOrgAddress={orgAddress && useOrgAddress}
      showLocationButtonText={showLocationButtonText}
      hideLocationButtonText={hideLocationButtonText}
      toggleSeparateFields={toggleSeparateFields}
      disableOrgAddressButton={disableOrgAddressButton}
      label={label}
    />
  );
}
